import { useEffect, useState, type FormEvent } from "react";
import { Toaster, toast } from "sonner";
import { NOMBRE_INSTALACION } from "@/reclutamiento/modo";
import { primerNombre } from "@/reclutamiento/logica";
import type { PortalApi } from "../api";
import type { UsuarioDemo } from "../api/tipos";
import { Logo } from "../iconos";
import { mensajeError } from "../util";

function Marco({ children }: { children: React.ReactNode }) {
  return (
    <div className="pt entrada">
      <div className="entrada-in">
        <div className="logo">
          <Logo /> {NOMBRE_INSTALACION}
        </div>
        {children}
      </div>
      <Toaster position="bottom-center" theme="system" toastOptions={{ style: { fontFamily: "var(--f-body)" } }} />
    </div>
  );
}

export function Entrada({ api, onListo }: { api: PortalApi; onListo: () => void }) {
  const [correo, setCorreo] = useState("");
  const [contrasena, setContrasena] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [demo, setDemo] = useState<UsuarioDemo[] | null>(null);
  const esDemo = api.modo === "demo";

  useEffect(() => {
    if (esDemo && api.usuariosDemo) api.usuariosDemo().then(setDemo).catch(() => setDemo([]));
  }, [api, esDemo]);

  const entrar = async (c: string, p: string) => {
    setEnviando(true);
    setError(null);
    try {
      await api.entrar(c, p);
      onListo();
    } catch (e) {
      setError(mensajeError(e, "No pudimos entrar. Revisa tu correo y contraseña."));
      setEnviando(false);
    }
  };

  const enviar = (e: FormEvent) => {
    e.preventDefault();
    if (!correo.trim()) return setError("Escribe tu correo.");
    if (!esDemo && !contrasena) return setError("Escribe tu contraseña.");
    entrar(correo, contrasena);
  };

  const olvide = async () => {
    if (!correo.trim()) return setError("Escribe tu correo arriba y te mandamos la liga para cambiar tu contraseña.");
    try {
      await api.recuperarContrasena(correo.trim());
      toast.success("Listo. Revisa tu correo: te mandamos una liga para crear una contraseña nueva.");
    } catch (e) {
      setError(mensajeError(e));
    }
  };

  return (
    <Marco>
      <div>
        <h1>Entra a tu portal</h1>
        <p className="muted" style={{ marginTop: 4 }}>
          Aquí ves a tus candidatos, sus fichas y en qué etapa van.
        </p>
      </div>

      {esDemo && (
        <>
          <p className="nota-demo">Modo demo: cualquier contraseña funciona.</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {demo === null && <div className="sk" style={{ height: 60 }} />}
            {demo?.map((u) => (
              <button key={u.correo} className="ucard" disabled={enviando} onClick={() => entrar(u.correo, "demo")}>
                <span className="av" aria-hidden="true">
                  {primerNombre(u.nombre).slice(0, 1)}
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <b>Entrar como {primerNombre(u.nombre)}</b>
                  <span>{u.cliente}</span>
                </span>
              </button>
            ))}
          </div>
          <div className="sep-o">o con tu correo</div>
        </>
      )}

      <form onSubmit={enviar} style={{ display: "flex", flexDirection: "column", gap: 12 }} noValidate>
        <label className="field">
          Correo
          <input className="inp" type="email" autoComplete="username" inputMode="email" value={correo} onChange={(e) => setCorreo(e.target.value)} />
        </label>
        <label className="field">
          Contraseña
          <input className="inp" type="password" autoComplete="current-password" value={contrasena} onChange={(e) => setContrasena(e.target.value)} />
        </label>
        {error && (
          <p role="alert" style={{ color: "var(--bad-ink)", fontSize: 14, fontWeight: 600 }}>
            {error}
          </p>
        )}
        <button className="btn primary big full" type="submit" disabled={enviando}>
          {enviando ? "Entrando…" : "Entrar"}
        </button>
        {!esDemo && (
          <button type="button" className="btn ghost" onClick={olvide}>
            ¿Olvidaste tu contraseña?
          </button>
        )}
      </form>
    </Marco>
  );
}

export function CrearContrasena({ api, onListo }: { api: PortalApi; onListo: () => void }) {
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    if (a.length < 8) return setError("Usa al menos 8 caracteres.");
    if (a !== b) return setError("Las dos contraseñas no son iguales.");
    setEnviando(true);
    setError(null);
    try {
      await api.definirContrasena(a);
      toast.success("Listo, ya tienes contraseña.");
      onListo();
    } catch (err) {
      setError(mensajeError(err));
      setEnviando(false);
    }
  };
  return (
    <Marco>
      <div>
        <h1>Crea tu contraseña</h1>
        <p className="muted" style={{ marginTop: 4 }}>
          Con ella vas a entrar a tu portal desde tu celular o tu computadora.
        </p>
      </div>
      <form onSubmit={enviar} style={{ display: "flex", flexDirection: "column", gap: 12 }} noValidate>
        <label className="field">
          Contraseña nueva
          <input className="inp" type="password" autoComplete="new-password" value={a} onChange={(e) => setA(e.target.value)} />
        </label>
        <label className="field">
          Escríbela otra vez
          <input className="inp" type="password" autoComplete="new-password" value={b} onChange={(e) => setB(e.target.value)} />
        </label>
        {error && (
          <p role="alert" style={{ color: "var(--bad-ink)", fontSize: 14, fontWeight: 600 }}>
            {error}
          </p>
        )}
        <button className="btn primary big full" type="submit" disabled={enviando}>
          {enviando ? "Guardando…" : "Guardar y entrar"}
        </button>
      </form>
    </Marco>
  );
}
